import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadSection } from "../../services/contentSource";
import heroFallback from "../../data/home/heroContent.json";

const HeroSection: React.FC = () => {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    loadSection("hero", heroFallback as any).then(({ data: main }) => setData(main));
  }, []);

  if (!data) return null;

  const metrics = data.metrics || [];
  const hud = data.visual?.hud || {};

  return (
    <section className="mx-hero-concept3">
      <div className="container mx-container">
        <div className="mx-hero-concept3-grid">
          {/* Left Column: Typography, Actions, and Metrics */}
          <div className="mx-hero-concept3-content">
            <div className="mx-hero-badge">
              <span className="mx-hero-badge-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </span>
              <span>{data.badgeText}</span>
            </div>

            <h1 className="mx-hero-concept3-title">
              {data.titleLine1}{" "}
              <br className="mx-hero-desktop-br" />
              {data.titleLine2}{" "}
              <br className="mx-hero-desktop-br" />
              <span className="mx-hero-concept3-title-accent">{data.titleAccent}</span>
            </h1>

            <p className="mx-hero-concept3-lede">
              {data.lede}
            </p>

            <div className="mx-hero-concept3-actions">
              <Link to={data.primaryCta?.href || "/products"} className="mx-hero-btn-primary">
                <span>{data.primaryCta?.text}</span>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </Link>
              <Link to={data.secondaryCta?.href || "/contact"} className="mx-hero-btn-secondary">
                <span>{data.secondaryCta?.text}</span>
              </Link>
            </div>

            {/* Bottom Metrics Bar */}
            <div className="mx-hero-concept3-metrics">
              {metrics.map((metric: any, index: number) => (
                <React.Fragment key={index}>
                  {index > 0 && <div className="mx-hero-metric-divider"></div>}
                  <div className="mx-hero-metric-item">
                    {index === 0 && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    )}
                    {index === 1 && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    )}
                    {index === 2 && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                        <polyline points="2 17 12 22 22 17"></polyline>
                        <polyline points="2 12 12 17 22 12"></polyline>
                      </svg>
                    )}
                    <span className="mx-hero-metric-label">{metric.label}</span>
                    <span className="mx-hero-metric-val">{metric.value}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right Column: AI Engineering Operations Lab Card with HUD Telemetry */}
          <div className="mx-hero-concept3-visual">
            <div className="mx-hero-visual-card">
              <img
                src={data.visual?.image}
                alt={data.visual?.imageAlt}
                className="mx-hero-card-bg"
                loading="eager"
              />

              {/* HUD Overlays */}
              {/* Top Left: Model Accuracy & Latency */}
              <div className="mx-hero-hud mx-hero-hud-top-left">
                <div className="mx-hero-hud-stat">
                  <span className="mx-hero-hud-label">Accuracy</span>
                  <span className="mx-hero-hud-val">{hud.accuracy}</span>
                </div>
                <div className="mx-hero-hud-sep"></div>
                <div className="mx-hero-hud-stat">
                  <span className="mx-hero-hud-label">Latency</span>
                  <span className="mx-hero-hud-val mx-hero-hud-cyan">{hud.latency}</span>
                </div>
              </div>

              {/* Top Right: Status Optimal with live wave */}
              <div className="mx-hero-hud mx-hero-hud-top-right">
                <div className="mx-hero-hud-row">
                  <span className="mx-hero-hud-label">Status:</span>
                  <span className="mx-hero-hud-pill-emerald">{hud.status}</span>
                </div>
                <div className="mx-hero-hud-wave">
                  <svg width="80" height="18" viewBox="0 0 80 18" fill="none">
                    <path d="M2 9 Q 12 2, 22 9 T 42 9 T 62 9 T 78 9" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              {/* Bottom Left: Sensor Data Feed & Vision Stream */}
              <div className="mx-hero-hud mx-hero-hud-bottom-left">
                <div className="mx-hero-hud-feed-row">
                  <span className="mx-hero-hud-label">{hud.sensorLabel}</span>
                  <svg width="84" height="14" viewBox="0 0 84 14" fill="none" className="mx-hero-hud-signal">
                    <path d="M1 7 L8 7 L12 2 L16 12 L20 3 L24 11 L28 7 L36 7 L40 1 L44 13 L48 5 L52 9 L56 7 L66 7 L70 3 L74 11 L78 7 L83 7" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="mx-hero-hud-feed-row">
                  <span className="mx-hero-hud-label">{hud.imageStreamLabel}</span>
                  <svg width="84" height="14" viewBox="0 0 84 14" fill="none" className="mx-hero-hud-signal">
                    <path d="M1 7 L10 7 L14 3 L18 11 L22 5 L26 9 L30 7 L42 7 L46 2 L50 12 L54 6 L58 8 L62 7 L74 7 L78 4 L82 10 L83 7" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              {/* Bottom Right: Node Active Badges */}
              <div className="mx-hero-hud mx-hero-hud-bottom-right">
                <div className="mx-hero-hud-badge-row">
                  <span className="mx-hero-hud-pill-dark">Status: <strong>{hud.status}</strong></span>
                </div>
                <div className="mx-hero-hud-badge-row">
                  <span className="mx-hero-hud-pill-active">
                    <span className="mx-hero-hud-dot-pulse"></span>
                    {hud.node}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
